/*@flow*/
import gql from 'graphql-tag';
import type { DocumentNode } from 'graphql';

export const PlaceMetricSchema = (gql(`
  # #################################
  #                                 #
  # --------- New types ---------   #
  #                                 #
  # #################################

  enum PlaceMetricFormulaDataviewOperation {
    count
    sum
    avg
  }
  type PlaceMetricFormulaDataview {
    column: String!
    operation: PlaceMetricFormulaDataviewOperation!
  }
  type PlaceMetricLayer {
    cartocss: String
  }
  type PlaceMetricAnalysisField {
    name: String!
    label: String!
  }
  type PlaceMetricAnalysis {
    id: String!
    name: String!
    table: String!
    sql: String!
    fields: [PlaceMetricAnalysisField]!
  }

  type PlaceMetric {
    id: String!
    name: String!
    description: String
    source: String
    analysis: PlaceMetricAnalysis!
    dataview: PlaceMetricFormulaDataview!
    prefix: String
    postfix: String
    valueFormula: String
    custom: Boolean!
  }

  # #################################
  #                                 #
  # --------- Inputs ---------   #
  #                                 #
  # #################################

  input PlaceMetricFormulaDataviewInput {
    column: String!
    operation: PlaceMetricFormulaDataviewOperation!
  }

  input PlaceMetricLayerInput {
    cartocss: String!
  }

  input PlaceMetricAnalysisFieldInput {
    name: String!
    label: String!
  }

  input PlaceMetricUpdateAnalysisInput {
    id: String!
    name: String
    table: String
    sql: String
    fields: [PlaceMetricAnalysisFieldInput]
  }

  input PlaceMetricCreateAnalysisInput {
    table: String!
    sql: String!
  }

  input PlaceMetricCreateInput {
    name: String!
    description: String
    source: String
    dataview: PlaceMetricFormulaDataviewInput!
    analysis: PlaceMetricCreateAnalysisInput!
    prefix: String
    postfix: String
    valueFormula: String
    custom: Boolean!
  }

  input PlaceMetricUpdateInput {
    id: String!
    name: String
    description: String
    source: String
    dataview: PlaceMetricFormulaDataviewInput
    analysis: PlaceMetricUpdateAnalysisInput
    prefix: String
    postfix: String
    valueFormula: String
    custom: Boolean!
  }

  input PlaceMetricDestroyInput {
    id: String!
  }

  input SelectedMetricsUpdateInput {
    ids: [String!]!
  }

  # #################################
  #                                 #
  # --------- Payloads ---------    #
  #                                 #
  # #################################

  type PlaceMetricCreatePayload {
    success: Boolean!
    record: PlaceMetric
    errors: [String]!
  }
  type PlaceMetricUpdatePayload {
    success: Boolean!
    record: PlaceMetric
    errors: [String]!
  }
  type PlaceMetricDestroyPayload {
    success: Boolean!
    errors: [String]!
  }

  # #################################
  #                                 #
  # --------- Extensions ---------  #
  #                                 #
  # #################################

  extend type Company {
    placeMetrics: [PlaceMetric]!
    placeMetricAnalyses: [PlaceMetricAnalysis]!
  }

  extend type QueryRoot {
    version: Int
    selectedMetrics: [String]!
    placeMetricsOrder: [String]!
  }

  extend type MutationRoot {
    createPlaceMetric(input: PlaceMetricCreateInput!): PlaceMetricCreatePayload
    updatePlaceMetric(input: PlaceMetricUpdateInput!): PlaceMetricUpdatePayload
    destroyPlaceMetric(
      input: PlaceMetricDestroyInput!
    ): PlaceMetricDestroyPayload

    updateSelectedMetrics(
      input: SelectedMetricsUpdateInput!
    ): PlaceMetricDestroyPayload
    updatePlaceMetricsOrder(
      input: SelectedMetricsUpdateInput!
    ): PlaceMetricDestroyPayload
  }
`): DocumentNode);

export const GET_PLACE_METRICS = (gql(`
  query GetPlaceMetrics {
    me {
      id
      company {
        id
        placeMetrics @client {
          id
          description
          name
          dataview {
            column
            operation
          }
          analysis {
            id
            table
            sql
          }
          prefix
          postfix
          valueFormula
          custom
        }
      }
    }
  }
`): DocumentNode);

export const GET_STARTUP = (gql(`
  query GetStartup {
    version
    selectedMetrics
    placeMetricsOrder
    me {
      id
      company {
        id
        placeMetricAnalyses @client {
          name
          table
          fields {
            name
            label
          }
        }
        placeMetrics @client {
          id
          description
          name
          dataview {
            column
            operation
          }
          analysis {
            id
            table
            sql
          }
          prefix
          postfix
          valueFormula
          custom
        }
      }
    }
  }
`): DocumentNode);

export const CREATE_METRIC = (gql(`
  mutation CreatePlaceMetric($input: PlaceMetricCreateInput!) {
    createPlaceMetric(input: $input) @client(always: true) {
      success
      errors
      record {
        id
      }
    }
  }
`): DocumentNode);

export const UPDATE_METRIC = (gql(`
  mutation UpdatePlaceMetric($input: PlaceMetricUpdateInput!) {
    updatePlaceMetric(input: $input) @client(always: true) {
      success
      errors
      record {
        id
      }
    }
  }
`): DocumentNode);

export const DESTROY_METRIC = (gql(`
  mutation DestroyPlaceMetric($input: PlaceMetricDestroyInput!) {
    destroyPlaceMetric(input: $input) @client(always: true) {
      success
      errors
    }
  }
`): DocumentNode);

export const UPDATE_SELECTED_METRICS = (gql(`
  mutation UpdateSelectedMetrics($input: SelectedMetricsUpdateInput!) {
    updateSelectedMetrics(input: $input) @client(always: true) {
      success
      errors
    }
  }
`): DocumentNode);

export const UPDATE_METRIC_ORDER = (gql(`
  mutation UpdatePlaceMetricsOrder($input: SelectedMetricsUpdateInput!) {
    updatePlaceMetricsOrder(input: $input) @client(always: true) {
      success
      errors
    }
  }
`): DocumentNode);

export const GET_PLACEMETRICS_ORDER = (gql(`
  query GetPlaceMetricOrder {
    placeMetricsOrder
  }
`): DocumentNode);

export const GET_SELECTED_METRICS = (gql(`
  query GetSelectedMetrics {
    selectedMetrics
  }
`): DocumentNode);
